import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { LoanNotes } from "@app/models/loan-notes";
import { LoansNotesService } from "@app/services/loans-notes.service";

@Component({
    selector: "app-select-loan-note-popup",
    templateUrl: "./select-loan-note-popup.component.html",
    styleUrls: ["./select-loan-note-popup.component.css"],
    standalone: false
})
export class SelectLoanNotePopupComponent implements OnInit {
  @Input() loanNotes: Array<LoanNotes>;
  @Input() displaySearchDialog: boolean;
  @Input() selectedLoanNotes: Array<LoanNotes>;
  @Output() onSubmit = new EventEmitter<Array<LoanNotes>>();
  @Output() onCancel = new EventEmitter();

  constructor(private loanNotesService: LoansNotesService) {}

  ngOnInit() {
    if (!this.selectedLoanNotes) {
      this.selectedLoanNotes = [];
    } else {
      this.selectedLoanNotes = [...this.selectedLoanNotes];
    }
    this.addMandatoryNotes();
    this.syncInitialSelection();
  }

  private addMandatoryNotes() {
    for (const root of this.loanNotes || []) for (const note of this.findAllChidren(root)) if (note.mandatory && note.status !== 'INACTIVE') for (const child of this.findAllChidren(note)) if (child.status !== 'INACTIVE' && !this.selectedLoanNotes.some((selected) => selected.id === child.id)) this.selectedLoanNotes.push(child);
  }

  syncInitialSelection() {
    if (!this.loanNotes || this.loanNotes.length === 0) {
      return;
    }

    const currentList = [...this.selectedLoanNotes];
    for (const note of currentList) {
      const fullNote = this.loanNotesService.findLoanNoteById(note.id, this.loanNotes) || note;
      const allChildren = this.findAllChidren(fullNote);
      for (const child of allChildren) {
        if (!this.selectedLoanNotes.some((n) => n.id === child.id)) {
          this.selectedLoanNotes.push(child);
        }
      }
    }

    this.updateAllParentsSelection();
  }

  hideSearch() {
    this.onCancel.emit();
  }

  onSelect(loanNote: LoanNotes) {
    if (!loanNote) {
      return;
    }

    const fullNote = this.loanNotesService.findLoanNoteById(loanNote.id, this.loanNotes) || loanNote;
    const allDescendants = this.findAllChidren(fullNote);

    const updated = [...this.selectedLoanNotes];
    for (const item of allDescendants) {
      if (!updated.some((p) => p.id === item.id)) {
        updated.push(item);
      }
    }
    this.selectedLoanNotes = updated;

    this.updateAllParentsSelection();
  }

  onSave() {
    this.onSubmit.emit(this.selectedLoanNotes);
  }

  deleteSelectedLoanNote(loanNote: LoanNotes) {
    if (!loanNote) {
      return;
    }

    const fullNote = this.loanNotesService.findLoanNoteById(loanNote.id, this.loanNotes) || loanNote;
    const allDescendants = this.findAllChidren(fullNote);
    const removeIds = new Set(allDescendants.map((item) => item.id));

    this.selectedLoanNotes = this.selectedLoanNotes.filter((item) => !removeIds.has(item.id));
  }

  updateAllParentsSelection() {
    if (!this.loanNotes || this.loanNotes.length === 0) {
      return;
    }

    const selectedSet = new Set(this.selectedLoanNotes.map((n) => n.id));

    const checkNode = (node: LoanNotes): boolean => {
      if (!node.subLoanNotes || node.subLoanNotes.length === 0) {
        return selectedSet.has(node.id);
      }

      let allChildrenSelected = true;
      for (const child of node.subLoanNotes) {
        const childSelected = checkNode(child);
        if (!childSelected) {
          allChildrenSelected = false;
        }
      }

      if (allChildrenSelected) {
        selectedSet.add(node.id);
      } else {
        selectedSet.delete(node.id);
      }

      return allChildrenSelected;
    };

    for (const rootNode of this.loanNotes) {
      checkNode(rootNode);
    }

    const newSelected: LoanNotes[] = [];
    for (const item of this.selectedLoanNotes) {
      if (selectedSet.has(item.id) && !newSelected.some((n) => n.id === item.id)) {
        newSelected.push(item);
      }
    }
    for (const id of selectedSet) {
      if (!newSelected.some((n) => n.id === id)) {
        const found = this.loanNotesService.findLoanNoteById(id, this.loanNotes);
        if (found) {
          newSelected.push(found);
        }
      }
    }

    this.selectedLoanNotes = newSelected;
  }

  findAllChidren(loanNote: LoanNotes): LoanNotes[] {
    let items: LoanNotes[] = [];
    if (loanNote) {
      items.push(loanNote);
      if (loanNote.subLoanNotes && loanNote.subLoanNotes.length > 0) {
        loanNote.subLoanNotes.forEach((p) => {
          items = items.concat(this.findAllChidren(p));
        });
      }
    }
    return items;
  }
}
